import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, Flame, MapPin, Utensils, Info, BookOpen } from 'lucide-react';
import { useSEO, slugify, SITE_URL } from '@/lib/seo';
import { DishImage } from '@/components/DishImage';
import { RatingStars } from '@/components/RatingStars';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { POSTS } from '@/lib/blogPosts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COURSE_LABEL = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  drinks: 'Drink',
  desserts: 'Dessert',
  accompaniment: 'Side dish',
  crazy: 'Indo-fusion',
};

/** Ingredient name without its quantity or prep clause — used in prose only. */
export const plainIngredient = (s) =>
  String(s || '')
    .replace(/^[\d\s./¼½¾-]+/, '')
    .replace(/^(cups?|tbsp|tbsps|tsp|tsps|grams?|g|gm|kgs?|ml|litres?|l|pinch(es)?|sprigs?|large|medium|small|inch|cloves?|nos?)\s+/i, '')
    .replace(/\s*\(.*?\)\s*/g, ' ')
    .split(',')[0]
    .trim();

const toISO8601 = (value) => {  if (!value) return undefined;
  const str = String(value).toLowerCase();
  const h = parseInt((str.match(/(\d+)\s*h/) || [])[1] || '0', 10);
  const m = parseInt((str.match(/(\d+)\s*m/) || [])[1] || '0', 10);
  if (!h && !m) return undefined;
  return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}`;
};

export default function RecipePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [ratingAgg, setRatingAgg] = useState({ avg: 0, count: 0 });

  const onRatingChange = useCallback((agg) => setRatingAgg(agg), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/recipes`);
        if (cancelled) return;
        const match = data.find((r) => slugify(r.name) === slug);
        if (!match) {
          setNotFound(true);
        } else {
          setRecipe(match);
          const pool = data.filter((r) => slugify(r.name) !== slug);
          const sameRegion = match.region
            ? pool.filter((r) => r.region === match.region)
            : pool.filter((r) => !r.region && r.category === match.category);
          const fallback = pool.filter((r) => r.category === match.category);
          const picked = [];
          const seen = new Set();
          [...sameRegion, ...fallback].forEach((r) => {
            const s = slugify(r.name);
            if (!seen.has(s) && picked.length < 6) {
              seen.add(s);
              picked.push(r);
            }
          });
          setRelated(picked);
        }
      } catch (e) {
        setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const featuredIn = useMemo(
    () => POSTS.filter((p) => (p.relatedRecipes || []).includes(slug)),
    [slug]
  );

  const description = useMemo(() => {
    if (!recipe) return 'Authentic Indian recipe';
    const course = (COURSE_LABEL[recipe.category] || recipe.category).toLowerCase();
    return `How to make ${recipe.name} — ${recipe.region ? `a ${recipe.region} Indian` : 'an Indian'} ${course} dish. Ingredients, step-by-step method, about ${recipe.prep_time} and an estimated ${recipe.calories} kcal per serving.`;
  }, [recipe]);

  const jsonLd = useMemo(() => {
    if (!recipe) return null;
    const totalTime = toISO8601(recipe.prep_time);
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Recipe',
          name: recipe.name,
          description,
          recipeCategory: COURSE_LABEL[recipe.category] || recipe.category,
          recipeCuisine: recipe.region ? `${recipe.region} Indian` : 'Indian',
          recipeIngredient: recipe.ingredients,
          recipeInstructions: (recipe.instructions || []).map((step, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            text: step,
          })),
          keywords: [recipe.name, recipe.region, COURSE_LABEL[recipe.category], 'Indian recipe']
            .filter(Boolean)
            .join(', '),
          totalTime,
          nutrition: {
            '@type': 'NutritionInformation',
            calories: `${recipe.calories} kcal (estimated)`,
          },
          image: recipe.image_url ? [`${SITE_URL}${recipe.image_url}`] : undefined,
          author: { '@type': 'Organization', name: 'Masala Spin', url: SITE_URL },
          publisher: { '@type': 'Organization', name: 'Masala Spin', url: SITE_URL },
          url: `${SITE_URL}/recipe/${slug}`,
          mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/recipe/${slug}` },
        },
      ],
    };
    if (ratingAgg.count > 0) {
      schema['@graph'][0].aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: ratingAgg.avg.toFixed(1),
        ratingCount: ratingAgg.count,
        bestRating: '5',
        worstRating: '1',
      };
    }
    return schema;
  }, [recipe, slug, ratingAgg, description]);

  useSEO({
    title: recipe
      ? `${recipe.name} Recipe — ${recipe.prep_time} | Masala Spin`
      : 'Recipe | Masala Spin',
    description,
    canonical: `${SITE_URL}/recipe/${slug}`,
    image: recipe?.image_url ? `${SITE_URL}${recipe.image_url}` : undefined,
    jsonLd,
    noindex: notFound,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#1C1917]/60 font-['Outfit']">
        Loading recipe…
      </div>
    );
  }

  if (notFound || !recipe) {
    return (
      <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-4">
            We could not find that recipe
          </h1>
          <p className="font-['Outfit'] text-[#1C1917]/70 mb-8">
            The link may be out of date. Try the recipe wheel, or browse our cooking guides.
          </p>
          <div className="flex flex-wrap gap-3 justify-center font-['Outfit']">
            <button
              onClick={() => navigate('/')}
              data-testid="back-home-from-404"
              className="px-6 py-3 bg-[#F97316] text-white rounded-full"
            >
              Back to Masala Spin
            </button>
            <Link to="/blog" className="px-6 py-3 border border-[#1C1917]/15 rounded-full text-[#1C1917]">
              Recipe blog
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const course = COURSE_LABEL[recipe.category] || recipe.category;

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteHeader />

      <main>
        <section className="max-w-5xl mx-auto px-6 pt-8">
          <Breadcrumbs
            items={[
              { name: 'Recipes', to: '/#meal-spinner' },
              ...(recipe.region ? [{ name: recipe.region, to: '/#cuisines' }] : []),
              { name: recipe.name },
            ]}
          />
        </section>

        <section className="pb-10 px-6">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <div>
              {recipe.region && (
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#F97316]/10 text-[#F97316] rounded-full text-xs font-['Outfit'] font-semibold uppercase tracking-widest mb-4">
                  <MapPin className="w-3 h-3" aria-hidden="true" /> {recipe.region}
                </span>
              )}
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1917] tracking-tight mb-5 leading-tight">
                {recipe.name}
              </h1>
              <p className="text-base text-[#1C1917]/70 font-['Outfit'] leading-relaxed mb-6">
                {recipe.name} is {recipe.region ? `a ${recipe.region} Indian` : 'an Indian'}{' '}
                {course.toLowerCase()} dish made with{' '}
                {recipe.ingredients.slice(0, 4).map(plainIngredient).join(', ').toLowerCase()} and other everyday
                ingredients. The full ingredient list and method are below.
              </p>

              <dl className="grid grid-cols-2 gap-4 font-['Outfit'] mb-6">
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-[#1C1917]/45 mb-1">
                    Total time (approx.)
                  </dt>
                  <dd className="flex items-center gap-2 text-[#2D4A22] font-medium">
                    <Clock className="w-4 h-4" aria-hidden="true" /> {recipe.prep_time}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-[#1C1917]/45 mb-1">
                    Calories (estimate)
                  </dt>
                  <dd className="flex items-center gap-2 text-[#F97316] font-medium">
                    <Flame className="w-4 h-4" aria-hidden="true" /> ~{recipe.calories} kcal
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-[#1C1917]/45 mb-1">Course</dt>
                  <dd className="flex items-center gap-2 text-[#1C1917]/80 font-medium">
                    <Utensils className="w-4 h-4" aria-hidden="true" /> {course}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-[#1C1917]/45 mb-1">Cuisine</dt>
                  <dd className="text-[#1C1917]/80 font-medium">
                    {recipe.region ? `${recipe.region} (Indian)` : 'Indian'}
                  </dd>
                </div>
              </dl>

              {recipe.dietary_tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {recipe.dietary_tags.map((t) => (
                    <span key={t} className="px-3 py-1 bg-[#2D4A22] text-white rounded-full text-xs font-['Outfit']">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              <div className="pt-4 border-t border-[#1C1917]/10">
                <p className="text-[10px] uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold mb-2">
                  Rate this recipe
                </p>
                <RatingStars recipeId={recipe.id} onChange={onRatingChange} />
              </div>
            </div>

            <div className="relative h-[300px] sm:h-[360px] rounded-3xl overflow-hidden shadow-xl">
              <DishImage
                src={recipe.image_url}
                alt={`${recipe.name} — ${recipe.region ? `${recipe.region} ` : ''}Indian ${course.toLowerCase()} dish served ready to eat`}
                fallbackInitial={recipe.name?.[0] || 'M'}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>
        </section>

        <section className="py-8 px-6">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-3">
                Ingredients
              </h2>
              <p className="text-xs text-[#1C1917]/55 font-['Outfit'] mb-5">
                {recipe.ingredients.filter((i) => /\d/.test(i)).length >= recipe.ingredients.length / 2
                  ? `Quantities below are a guide for a typical household portion — scale them to the number of people you are cooking for and season to taste.`
                  : `What goes into ${recipe.name}. This recipe lists ingredients without measured quantities — scale them to the number of people you are cooking for and season to taste.`}
              </p>
              <ul className="space-y-3 font-['Outfit']">
                {recipe.ingredients.map((i, idx) => (
                  <li key={`${i}-${idx}`} className="flex items-start gap-3 text-[#1C1917]/85">
                    <span className="w-2 h-2 rounded-full bg-[#F97316] mt-2 flex-shrink-0" aria-hidden="true" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-5">
                Method
              </h2>
              <ol className="space-y-5 font-['Outfit']">
                {recipe.instructions.map((step, idx) => (
                  <li key={`step-${idx}`} className="flex gap-4">
                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#F97316] text-white font-['Playfair_Display'] font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <span className="text-[#1C1917]/85 leading-relaxed pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="pb-4 px-6">
          <div
            className="max-w-5xl mx-auto p-5 rounded-2xl border border-[#F97316]/25 bg-[#F97316]/5 font-['Outfit'] text-sm text-[#1C1917]/75 leading-relaxed"
            data-testid="recipe-estimates-note"
          >
            <p className="flex items-start gap-2">
              <Info className="w-4 h-4 text-[#F97316] mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                <strong>About these numbers:</strong> the time shown is an approximate overall
                cooking time, and the calorie figure is a rough estimate for one serving of the dish
                as described — not a laboratory analysis. Both will change with your portion size,
                ingredients and how much fat you cook with. Dietary tags describe the dish in general
                terms; always check the labels on what you buy. Nothing here is medical or dietary
                advice. Spotted a mistake?{' '}
                <Link to="/contact?topic=recipe-correction" className="text-[#F97316] hover:underline">
                  Tell us and we will fix it
                </Link>.
              </span>
            </p>
          </div>
        </section>

        {featuredIn.length > 0 && (
          <section className="py-10 px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-5">
                Guides that feature this dish
              </h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {featuredIn.map((p) => (
                  <li key={p.slug}>
                    <Link
                      to={`/blog/${p.slug}`}
                      data-testid={`recipe-guide-${p.slug}`}
                      className="flex gap-3 p-4 rounded-xl bg-white border border-[#1C1917]/5 hover:border-[#F97316]/40 transition-colors h-full"
                    >
                      <BookOpen className="w-4 h-4 text-[#F97316] mt-1 flex-shrink-0" aria-hidden="true" />
                      <span>
                        <span className="block font-['Playfair_Display'] font-bold text-[#1C1917] leading-tight mb-1">
                          {p.title}
                        </span>
                        <span className="block text-xs text-[#1C1917]/55 font-['Outfit'] line-clamp-2">
                          {p.description}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="py-10 px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-5">
                {recipe.region ? `More ${recipe.region} recipes` : `More ${course.toLowerCase()} recipes`}
              </h2>
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {related.map((r) => (
                  <li key={r.id || r.name}>
                    <Link
                      to={`/recipe/${slugify(r.name)}`}
                      data-testid={`related-recipe-${slugify(r.name)}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#1C1917]/5 hover:border-[#F97316]/40 hover:shadow-md transition-all h-full"
                    >
                      <span className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 block">
                        <DishImage
                          src={r.image_url}
                          alt={`${r.name} recipe`}
                          fallbackInitial={r.name?.[0]}
                          className="w-full h-full"
                          imgClassName="w-full h-full object-cover"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-['Playfair_Display'] font-bold text-[#1C1917] text-sm leading-tight">
                          {r.name}
                        </span>
                        <span className="block text-xs text-[#1C1917]/55 font-['Outfit'] mt-0.5">
                          {r.prep_time} · ~{r.calories} kcal
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
